/**
 * <drawer-component>
 *
 * Standardni drawer Custom Element. Sve drawer-e u temi (cart, mobile menu,
 * filters, sort, local pickup, sidebar favorites) renderuju kroz
 * `{% render 'drawer' %}` snippet.
 *
 * `data-sync-overlay` makes the shared overlay opacity track drawer slide
 * progress. Motion values come from the global `--drawer-*` CSS tokens.
 * Top-level `popup:opening` events close open drawers without returning focus;
 * child popups remain stackable because they do not emit that takeover event.
 */

import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';
import { IOSScrollLock } from '@theme/ios-scroll-lock';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';

const TRANSITION_FALLBACK_MS = 300;

export class DrawerComponent extends HTMLElement {
  // Shared across ALL drawers: the body-level open state (scrollLock overflow
  // class + page overlay) is reference-counted so it stays active while any
  // drawer is open. A child submenu drawer closing (back) must NOT release the
  // lock/overlay while its parent menu is still open — only the last drawer to
  // close tears them down.
  static #openDrawerCount = 0;
  // Root drawers share one page overlay. Track which root currently controls its
  // animation state so a closing drawer cannot clear a newer drawer's overlay.
  static #overlayOwner = null;

  #scrollableEl = null;
  #overlayEl = null;
  #onBodyClick = null;
  #onOverlayClick = null;
  #onEscape = null;
  #onOtherDrawerOpening = null;
  #onOtherDrawerClosing = null;
  #onPopupOpening = null;
  // The trigger that opened this drawer — used to keep its aria-expanded in sync
  // and to return focus to it on close.
  #openerTrigger = null;
  // Shared synced-overlay + slide-animation bookkeeping.
  #overlaySyncRaf = null;
  #closeFallbackTimer = null;
  #focusTrapTimer = null;
  #openRaf1 = null;
  #openRaf2 = null;
  #onCloseTransitionEnd = null;
  #overlayHandoff = false;

  isOpen = false;

  connectedCallback() {
    if (this.hasAttribute('data-no-drawer')) return;

    this.#scrollableEl = this.querySelector('[data-scrollable]') || this;
    this.#overlayEl = document.querySelector('.page-overlay');

    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('aria-expanded', 'false');
      this.setAttribute('inert', '');
    }

    if (this.style.display !== 'none') {
      this.style.display = 'none';
    }

    this.classList.remove('is-open', 'is-closing');
    this.#bindTriggers();
  }

  disconnectedCallback() {
    // Always remove the document-level trigger listener, open or not.
    if (this.#onBodyClick) {
      document.body.removeEventListener('click', this.#onBodyClick);
      this.#onBodyClick = null;
    }
    this.#cancelOpenAnimation();
    this.#clearCloseAnimation();
    this.#stopOverlaySync();
    if (this.isOpen) {
      this.#unlockScroll();
      this.#detachGlobalListeners();
    }
    this.#releaseOverlayOwnership();
  }

  open(trigger = null) {
    if (this.isOpen) return;

    const beforeEvent = new CustomEvent('drawer:before-open', {
      bubbles: true,
      cancelable: true,
    });
    this.dispatchEvent(beforeEvent);
    if (beforeEvent.defaultPrevented) return;
    if (this.onBeforeOpen?.(beforeEvent) === false) return;

    this.#openerTrigger = trigger;

    this.#clearCloseAnimation();
    this.#cancelOpenAnimation();

    document.dispatchEvent(
      new CustomEvent('drawer:opening', {
        detail: { drawerId: this.id, isChild: this.dataset.childDrawer != null },
      })
    );

    this.isOpen = true;
    this.#syncTriggers(true);
    this.classList.remove('is-closing');
    this.style.display = '';
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');

    const syncOverlay = this.#usesSyncedOverlay();
    const animate = this.#isDrawerAnimated();

    if (this.dataset.childDrawer == null) {
      this.#claimOverlay(syncOverlay);
    } else {
      this.#overlayEl?.classList.add('is-visible');
    }

    if (animate) {
      this.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      void this.offsetHeight;

      this.#openRaf1 = requestAnimationFrame(() => {
        this.#openRaf1 = null;
        this.#openRaf2 = requestAnimationFrame(() => {
          this.#openRaf2 = null;
          if (!this.isOpen) return;

          this.setAttribute('aria-expanded', 'true');
          this.classList.add('is-open');
          if (syncOverlay) this.#runSyncedOverlay('open');
        });
      });
    } else {
      this.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (syncOverlay) {
        this.#setSyncedOverlayProgress(1);
      }
    }

    this.#attachGlobalListeners();
    this.#lockScroll();

    const focusDelay = this.#getDrawerTransitionMs() + 10;
    this.#focusTrapTimer = window.setTimeout(() => {
      this.#focusTrapTimer = null;
      if (this.isOpen) trapFocus(this);
    }, focusDelay);

    this.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true }));
    this.onOpened?.();
  }

  close(returnFocus = true) {
    if (!this.isOpen) return;

    const beforeEvent = new CustomEvent('drawer:before-close', {
      bubbles: true,
      cancelable: true,
    });
    this.dispatchEvent(beforeEvent);
    if (beforeEvent.defaultPrevented) return;
    if (this.onBeforeClose?.(beforeEvent) === false) return;

    this.isOpen = false;

    this.#syncTriggers(false);
    this.#cancelOpenAnimation();
    this.#stopOverlaySync();

    const syncOverlay = this.#usesSyncedOverlay();
    const animate = this.#isDrawerAnimated();

    // Tell child drawers (market / language / submenu levels) this drawer is
    // closing. When a NON-child (root menu) closes — e.g. its close-X targets
    // the root `Drawer`, not the open selector — any open child must close too.
    // Otherwise the child stays isOpen=true, keeps its +1 on the shared
    // reference-counted overlay / scroll-lock, and the overlay stays up with
    // nothing visible (CSS only hides the child, it doesn't update JS state).
    document.dispatchEvent(
      new CustomEvent('drawer:closing', {
        detail: { drawerId: this.id, isChild: this.dataset.childDrawer != null },
      })
    );

    this.classList.add('is-closing');
    this.classList.remove('is-open');
    this.setAttribute('aria-expanded', 'false');
    this.#openerTrigger?.setAttribute('aria-expanded', 'false');

    void this.offsetHeight;

    this.#detachGlobalListeners();
    this.#unlockScroll();

    // Return focus to the trigger that opened the drawer — unless we're closing
    // only because another drawer is taking over (it will claim focus itself).
    const opener = this.#openerTrigger;
    this.#openerTrigger = null;
    removeTrapFocus(returnFocus ? opener : null);

    if (!animate) {
      if (syncOverlay) {
        this.#clearSyncedOverlay();
      }
      this.#finalizeClosed();
      return;
    }

    const openProgress = this.#measureDrawerOpenProgress();
    const barelyVisible = openProgress <= 0.05;

    if (syncOverlay) {
      if (barelyVisible) {
        this.#clearSyncedOverlay();
      } else {
        this.#runSyncedOverlay('close');
      }
    }

    if (barelyVisible) {
      this.#finalizeClosed();
      return;
    }

    this.#waitForCloseTransition(syncOverlay);
  }

  toggle(trigger = null) {
    this.isOpen ? this.close() : this.open(trigger);
  }

  // Keep every trigger's aria-expanded in sync with the drawer state so disclosure
  // toggles reflect open/closed (e.g. the header hamburger -> close icon swap).
  #syncTriggers(expanded) {
    if (!this.id) return;
    document
      .querySelectorAll('[data-toggle-id="' + this.id + '"]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
  }

  #usesSyncedOverlay() {
    return this.dataset.syncOverlay === 'true';
  }

  #isDrawerAnimated() {
    if (this.dataset.drawerAnimate === 'false') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (document.documentElement.classList.contains('animationsDisabled')) return false;
    if (window.theme?.enableAnimations === false) return false;
    return true;
  }

  #getDrawerTransitionMs() {
    const styles = getComputedStyle(this);
    const raw = styles.getPropertyValue('--drawer-transition-duration').trim();
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed)) return TRANSITION_FALLBACK_MS;
    // A CSS <time> may be seconds ("0.3s") or milliseconds ("300ms"); normalise to ms
    // so the close-wait matches the actual slide (a bare "0.3s" read as 0.3ms would
    // finalise — display:none — before the slide-out finishes).
    if (/ms$/i.test(raw)) return parsed;
    if (/s$/i.test(raw)) return parsed * 1000;
    return parsed;
  }

  #getOverlayMaxOpacity() {
    const raw = getComputedStyle(this).getPropertyValue('--drawer-overlay-opacity').trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0.3;
  }

  #measureDrawerOpenProgress() {
    const rect = this.getBoundingClientRect();
    const clamp = (value, size) => Math.min(Math.max(value, 0), size) / size;

    if (this.classList.contains('Drawer--left')) {
      return clamp(rect.right, this.offsetWidth || 1);
    }

    if (this.classList.contains('Drawer--top')) {
      return clamp(rect.bottom, this.offsetHeight || 1);
    }

    if (
      this.classList.contains('Drawer--bottom') ||
      (this.classList.contains('Drawer--mobile-bottom') && window.innerWidth < 1025)
    ) {
      return clamp(window.innerHeight - rect.top, this.offsetHeight || 1);
    }

    return clamp(window.innerWidth - rect.left, this.offsetWidth || 1);
  }

  #cancelOpenAnimation() {
    if (this.#openRaf1) {
      cancelAnimationFrame(this.#openRaf1);
      this.#openRaf1 = null;
    }
    if (this.#openRaf2) {
      cancelAnimationFrame(this.#openRaf2);
      this.#openRaf2 = null;
    }
    if (this.#focusTrapTimer) {
      clearTimeout(this.#focusTrapTimer);
      this.#focusTrapTimer = null;
    }
  }

  #clearCloseAnimation() {
    if (this.#onCloseTransitionEnd) {
      this.removeEventListener('transitionend', this.#onCloseTransitionEnd);
      this.#onCloseTransitionEnd = null;
    }
    if (this.#closeFallbackTimer) {
      clearTimeout(this.#closeFallbackTimer);
      this.#closeFallbackTimer = null;
    }
    this.classList.remove('is-closing');
  }

  #prepareSyncedOverlay(preserveOpacity = false) {
    if (!this.#overlayEl) return;
    this.#overlayEl.style.display = 'block';
    this.#overlayEl.style.transition = 'none';
    this.#overlayEl.style.opacity = preserveOpacity ? String(this.#getOverlayMaxOpacity()) : '0';
    this.#overlayEl.classList.add('is-visible');
    this.#overlayEl.classList.add('page-overlay--synced');
  }

  #claimOverlay(syncOverlay) {
    const previousOwner = DrawerComponent.#overlayOwner;
    const currentOpacity = this.#overlayEl
      ? Number.parseFloat(getComputedStyle(this.#overlayEl).opacity)
      : 0;
    this.#overlayHandoff =
      previousOwner != null ||
      this.#overlayEl?.classList.contains('is-visible') ||
      (Number.isFinite(currentOpacity) && currentOpacity > 0);
    DrawerComponent.#overlayOwner = this;
    if (!this.#overlayEl) return;

    if (syncOverlay) {
      this.#prepareSyncedOverlay(this.#overlayHandoff);
      return;
    }

    // A regular root drawer can take over while a synced drawer is still
    // closing. Reset the previous owner's inline state and use the CSS fade.
    this.#overlayEl.classList.remove('page-overlay--synced');
    this.#overlayEl.style.display = '';
    this.#overlayEl.style.opacity = '';
    this.#overlayEl.style.transition = '';
    this.#overlayEl.classList.add('is-visible');
    this.#overlayHandoff = false;
  }

  #setSyncedOverlayProgress(progress) {
    if (!this.#overlayEl || DrawerComponent.#overlayOwner !== this) return;
    const clamped = Math.min(Math.max(progress, 0), 1);
    this.#overlayEl.style.opacity = String(clamped * this.#getOverlayMaxOpacity());
  }

  #clearSyncedOverlay() {
    this.#releaseOverlayOwnership();
  }

  #releaseOverlayOwnership() {
    if (DrawerComponent.#overlayOwner !== this) return;

    DrawerComponent.#overlayOwner = null;
    this.#overlayHandoff = false;
    if (!this.#overlayEl) return;

    this.#overlayEl.classList.remove('is-visible', 'page-overlay--synced');
    this.#overlayEl.style.display = '';
    this.#overlayEl.style.opacity = '';
    this.#overlayEl.style.transition = '';

    // Theme Editor removal can disconnect a root before its child drawer. Keep
    // the shared overlay visible until that final drawer releases its lock.
    if (DrawerComponent.#openDrawerCount > 0) {
      this.#overlayEl.classList.add('is-visible');
    }
  }

  #stopOverlaySync() {
    if (this.#overlaySyncRaf) {
      cancelAnimationFrame(this.#overlaySyncRaf);
      this.#overlaySyncRaf = null;
    }
  }

  #runSyncedOverlay(mode) {
    if (!this.#overlayEl) return;

    this.#stopOverlaySync();

    // During a root-to-root handoff the page must remain dimmed continuously;
    // fading from zero again would create a visible flash between drawers.
    if (mode === 'open' && this.#overlayHandoff) {
      this.#overlayHandoff = false;
      this.#setSyncedOverlayProgress(1);
      return;
    }

    this.#overlayHandoff = false;
    const maxOpacity = this.#getOverlayMaxOpacity();
    const startTime = performance.now();
    const maxDuration = this.#getDrawerTransitionMs() + 150;

    const tick = () => {
      if (DrawerComponent.#overlayOwner !== this) {
        this.#overlaySyncRaf = null;
        return;
      }

      if (mode === 'open' && !this.isOpen) {
        this.#overlaySyncRaf = null;
        return;
      }

      if (mode === 'close' && this.isOpen) {
        this.#overlaySyncRaf = null;
        return;
      }

      const progress = this.#measureDrawerOpenProgress();
      this.#overlayEl.style.opacity = String(progress * maxOpacity);

      const timedOut = performance.now() - startTime >= maxDuration;
      const isOpenDone = mode === 'open' && (progress >= 0.99 || timedOut);
      const isCloseDone = mode === 'close' && (progress <= 0.01 || timedOut);

      if (isOpenDone) {
        this.#overlayEl.style.opacity = String(maxOpacity);
        this.#overlaySyncRaf = null;
        return;
      }

      if (isCloseDone) {
        this.#overlayEl.style.opacity = '0';
        this.#overlaySyncRaf = null;
        return;
      }

      this.#overlaySyncRaf = requestAnimationFrame(tick);
    };

    this.#overlaySyncRaf = requestAnimationFrame(tick);
  }

  #waitForCloseTransition(syncOverlay) {
    this.#clearCloseAnimation();

    let finished = false;
    const duration = this.#getDrawerTransitionMs();

    const finalize = () => {
      if (finished) return;
      finished = true;
      this.#clearCloseAnimation();
      if (syncOverlay) {
        this.#clearSyncedOverlay();
      }
      this.#finalizeClosed();
    };

    this.#onCloseTransitionEnd = (event) => {
      if (event.target !== this) return;
      if (event.propertyName !== 'transform') return;
      finalize();
    };

    this.addEventListener('transitionend', this.#onCloseTransitionEnd);
    this.#closeFallbackTimer = window.setTimeout(finalize, duration + 100);
  }

  #finalizeClosed() {
    if (this.getAttribute('aria-hidden') === 'true' && this.style.display === 'none') {
      return;
    }

    this.classList.remove('is-open', 'is-closing');
    this.setAttribute('aria-expanded', 'false');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
    this.style.display = 'none';
    this.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
    this.onClosed?.();
  }

  #bindTriggers() {
    const id = this.id;
    if (!id) {
      console.warn('<drawer-component>: missing id', this);
      return;
    }

    // Stored on the instance so disconnectedCallback can remove it — otherwise
    // this document-level listener (and the reference it holds to this element)
    // leaks across Shopify Theme Editor section re-renders.
    this.#onBodyClick = (event) => {
      const trigger = event.target.closest('[data-toggle-id="' + id + '"]');
      if (!trigger) return;
      const action = trigger.dataset.action;
      if (!['open-drawer', 'close-drawer', 'toggle-drawer'].includes(action)) return;

      event.preventDefault();
      if (action === 'open-drawer') this.open(trigger);
      else if (action === 'close-drawer') this.close();
      else this.toggle(trigger);
    };
    document.body.addEventListener('click', this.#onBodyClick);
  }

  #attachGlobalListeners() {
    this.#onOverlayClick = () => this.close();
    this.#overlayEl?.addEventListener('click', this.#onOverlayClick);

    this.#onEscape = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.#onEscape);

    this.#onOtherDrawerOpening = (e) => {
      const { drawerId, isChild } = e.detail || {};
      if (drawerId === this.id) return;
      if (isChild) return;
      // The opening drawer will take focus; don't yank it back to this opener.
      this.close(false);
    };
    document.addEventListener('drawer:opening', this.#onOtherDrawerOpening);

    this.#onOtherDrawerClosing = (e) => {
      const { drawerId, isChild } = e.detail || {};
      if (drawerId === this.id) return;
      // Only a closing root/parent (non-child) drawer cascades to its open
      // children — so the shared overlay / scroll-lock count releases fully.
      // A child closing on its own (isChild) must NOT close its siblings/parent.
      if (!isChild && this.dataset.childDrawer != null) {
        this.close(false);
      }
    };
    document.addEventListener('drawer:closing', this.#onOtherDrawerClosing);

    this.#onPopupOpening = () => {
      this.close(false);
      if (this.isOpen) return;

      // A synced drawer normally owns the shared overlay until its slide-out
      // finishes. Release that ownership now so its delayed close cleanup
      // cannot hide or fade the overlay that the opening popup takes over.
      this.#stopOverlaySync();
      this.#releaseOverlayOwnership();
    };
    document.addEventListener('popup:opening', this.#onPopupOpening);
  }

  #detachGlobalListeners() {
    if (this.#onOverlayClick) {
      this.#overlayEl?.removeEventListener('click', this.#onOverlayClick);
      this.#onOverlayClick = null;
    }
    if (this.#onEscape) {
      document.removeEventListener('keydown', this.#onEscape);
      this.#onEscape = null;
    }
    if (this.#onOtherDrawerOpening) {
      document.removeEventListener('drawer:opening', this.#onOtherDrawerOpening);
      this.#onOtherDrawerOpening = null;
    }
    if (this.#onOtherDrawerClosing) {
      document.removeEventListener('drawer:closing', this.#onOtherDrawerClosing);
      this.#onOtherDrawerClosing = null;
    }
    if (this.#onPopupOpening) {
      document.removeEventListener('popup:opening', this.#onPopupOpening);
      this.#onPopupOpening = null;
    }
  }

  #lockScroll() {
    if (this.#scrollableEl) {
      disableBodyScroll(this.#scrollableEl, bodyScrollLockOptions);
    }
    DrawerComponent.#openDrawerCount += 1;
    document.body.classList.add('scrollLock');
    this.#overlayEl?.classList.add('is-visible');
    IOSScrollLock.lock();
  }

  #unlockScroll() {
    if (this.#scrollableEl) {
      enableBodyScroll(this.#scrollableEl);
    }
    DrawerComponent.#openDrawerCount = Math.max(0, DrawerComponent.#openDrawerCount - 1);
    // Only the last open drawer tears down the shared body-level state, so a child
    // submenu closing doesn't strip the lock/overlay while its parent is open.
    if (DrawerComponent.#openDrawerCount === 0) {
      document.body.classList.remove('scrollLock');

      // Synced drawers release the overlay after their slide-out completes.
      // Regular drawers use the normal CSS fade and release it immediately.
      if (DrawerComponent.#overlayOwner !== this || !this.#usesSyncedOverlay()) {
        this.#overlayEl?.classList.remove('is-visible');
      }
      if (DrawerComponent.#overlayOwner === this && !this.#usesSyncedOverlay()) {
        DrawerComponent.#overlayOwner = null;
      }
    }
    IOSScrollLock.unlock();
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}
